"use client";

import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import {
  Badge,
  Button,
  Check,
  ChevronDown,
  Copy,
  EmbedSnippet,
  Eye,
  Icon,
  Input,
  Link2,
  Lock,
  Modal,
  RotateCcw,
  SegmentedControl,
  Spinner,
  Tabs,
  Toggle,
  Trash2,
  cx,
} from "@/components";
// Imported directly (not via the "@/components" barrel above) so a test can
// mock just this module without also having to stub every other barrel
// export ShareModal pulls in — same import shape as SharePanel.tsx (D4).
import { useProUpsell } from "@/components/pro-modal-provider";
import type { ShareVisibility } from "@/lib/contracts/share";
import { DEFAULT_EMBED_OPTIONS, buildEmbedQuery, type EmbedOptions } from "@/lib/embed/params";
// D7 (MAP-123): reuse the account SharePanel's pure listbox/gating helpers
// rather than re-deriving them — see the file header note below for why this
// is a local listbox implementation (not a shared component) despite sharing
// that logic. SharePanel itself is NOT modified by this import.
import { isGatedVisibility, nextVisibilityHighlight, resolveVisibilitySelection } from "../../(account)/maps/[id]/SharePanel";
import { QrCode } from "./QrCode";
import "./share.css";

/** The minted share, as the /api/maps endpoints hand it back (origin-derived URLs). */
export interface ShareInfo {
  slug: string;
  url: string;
  embedUrl: string;
  visibility: ShareVisibility;
  viewersCanExport: boolean;
  lastPublishedAt: string;
}

export interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  /** White-label brand name — from getTheme via the page, never a literal. */
  brandName: string;
  mapTitle: string;
  share: ShareInfo | null;
  minting: boolean;
  mintError: string | null;
  onRetry: () => void;
  onSetVisibility: (visibility: ShareVisibility) => Promise<void>;
  /** Tier-derived (analysisEntitlement.isPaid) — Unlisted/Private visibility is Pro, same gate as the account SharePanel (D4). */
  isPaid: boolean;
  onRotate: () => Promise<void>;
  onRevoke: () => Promise<void>;
  /** Owner toggle: whether viewers may export the map (AC-6, default off). */
  viewersCanExport: boolean;
  onSetViewersCanExport: (value: boolean) => Promise<void>;
  /** Fired when the embed snippet is copied — the embed_snippet_copied analytics hook. */
  onSnippetCopy?: () => void;
  /**
   * Auth state (from the page's getCurrentAccount). Sharing is account-gated:
   * when false the link is masked, the QR is withheld, and Copy link routes to
   * sign-up — the share UI stays visible, the shareable URL does not.
   */
  signedIn: boolean;
}

const VISIBILITY_HINTS: Record<string, string> = {
  unlisted: "Anyone with the link can view. The map stays out of search engines and off your profile.",
  public: "Anyone can find and view this map — public maps are indexable by search engines.",
  private: "Only you can open this map. The link stops working for everyone else.",
};

/**
 * The visibility listbox's option set (D7, MAP-123) — same three values, same
 * order, as the account SharePanel's `VISIBILITIES` (D4), so a map's stored
 * visibility always matches a real option here too (a map set to Private via
 * the account side previously had no matching option in this modal's old
 * two-value SegmentedControl). Short row hints; the longer `VISIBILITY_HINTS`
 * sentence below the control stays editor-specific copy.
 */
const VISIBILITY_OPTIONS: Array<{ value: ShareVisibility; label: string; hint: string }> = [
  { value: "public", label: "Public", hint: "Indexable by search engines." },
  { value: "unlisted", label: "Unlisted", hint: "Anyone with the link can view." },
  { value: "private", label: "Private", hint: "Only you can open it." },
];

/** Builder defaults mirror the concept: zoom + filter + legend + fullscreen on. */
const STANDARD_PRESET: EmbedOptions = {
  ...DEFAULT_EMBED_OPTIONS,
  filter: true,
  legend: true,
  fullscreen: true,
};

const LOCATOR_PRESET: EmbedOptions = {
  ...DEFAULT_EMBED_OPTIONS,
  filter: false,
  search: true,
  legend: false,
  fullscreen: true,
  mode: "store_locator",
};

/**
 * Mask the unique slug inside a share URL so a signed-out viewer can't read or
 * copy the live link — the origin/path shape stays legible, the token doesn't.
 */
function maskShareUrl(url: string, slug: string): string {
  const dots = "•".repeat(Math.max(slug.length, 6));
  return url.includes(slug) ? url.replace(slug, dots) : dots;
}

/**
 * Relative "time since publish" label, or `null` when there's nothing to
 * report. `lastPublishedAt` isn't always a real timestamp — the owner-open
 * rehydration flow ((editor)/page.tsx's `resolveOwnerMapForEditor`) hands
 * back `""` for it deliberately (not carried by `getOwnerShare`; a re-save
 * recomputes it server-side) — so `new Date("").getTime()` is `NaN` and must
 * fail closed here instead of propagating to a rendered "NaNd ago".
 */
function publishedAgo(iso: string): string | null {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return null;
  const s = Math.max(0, (Date.now() - ms) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/**
 * The M6 share modal — Link tab (copy + QR + visibility + revoke/rotate) and
 * the live-preview embed builder (toggles → querystring → iframe + snippet,
 * updating together). Attribution is the one switch that doesn't flip.
 */
export function ShareModal({
  open,
  onClose,
  brandName,
  mapTitle,
  share,
  minting,
  mintError,
  onRetry,
  onSetVisibility,
  isPaid,
  onRotate,
  onRevoke,
  viewersCanExport,
  onSetViewersCanExport,
  onSnippetCopy,
  signedIn,
}: ShareModalProps) {
  const { openProUpsell } = useProUpsell();
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<"visibility" | "rotate" | "revoke" | "export" | null>(null);
  const [revokeArmed, setRevokeArmed] = useState(false);

  // Visibility listbox (D7, MAP-123) — same hand-rolled dropdown convention as
  // the account SharePanel (D4): local open/highlight state, a `document`
  // mousedown listener for click-outside, roving ArrowUp/Down, Enter/Space to
  // select, Escape closes + returns focus. `share` can be null (still
  // minting), so `currentVisibility` falls back to "public" — the listbox
  // only ever renders once `share` is truthy (inside `visibilityControl`
  // below, which stays visible regardless of visibility — see D8/MAP-124).
  const currentVisibility: ShareVisibility = share?.visibility ?? "public";
  const [visMenuOpen, setVisMenuOpen] = useState(false);
  const [visHighlight, setVisHighlight] = useState(0);
  const visContainerRef = useRef<HTMLDivElement>(null);
  const visTriggerRef = useRef<HTMLButtonElement>(null);
  const visListboxRef = useRef<HTMLDivElement>(null);
  const visListboxId = useId();

  useEffect(() => {
    if (!visMenuOpen) return;
    visListboxRef.current?.focus();
    function onDocMouseDown(e: MouseEvent) {
      if (visContainerRef.current && !visContainerRef.current.contains(e.target as Node)) setVisMenuOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [visMenuOpen]);

  function openVisMenu() {
    const idx = VISIBILITY_OPTIONS.findIndex((v) => v.value === currentVisibility);
    setVisHighlight(idx === -1 ? 0 : idx);
    setVisMenuOpen(true);
  }

  function closeVisMenu(returnFocus: boolean) {
    setVisMenuOpen(false);
    if (returnFocus) visTriggerRef.current?.focus();
  }

  function selectVisibility(next: ShareVisibility) {
    if (busy !== null) return;
    closeVisMenu(true);
    const outcome = resolveVisibilitySelection(next, currentVisibility, isPaid);
    if (outcome.kind === "gated") {
      // Client-side gate — never reaches the API for a free/signed-out
      // selection. The server's own 402 (MAP-121) stays as a defensive
      // fallback in IngestEditor's handleSetVisibility for a stale client.
      openProUpsell({ trigger: "private_share" });
      return;
    }
    if (outcome.kind === "noop") return;
    void run("visibility", () => onSetVisibility(outcome.visibility));
  }

  function onVisTriggerClick() {
    if (visMenuOpen) closeVisMenu(false);
    else openVisMenu();
  }

  function onVisTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (visMenuOpen) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openVisMenu();
    }
  }

  function onVisListboxKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setVisHighlight((i) => nextVisibilityHighlight(i, 1, VISIBILITY_OPTIONS.length));
        break;
      case "ArrowUp":
        e.preventDefault();
        setVisHighlight((i) => nextVisibilityHighlight(i, -1, VISIBILITY_OPTIONS.length));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        selectVisibility(VISIBILITY_OPTIONS[visHighlight]!.value);
        break;
      case "Escape":
        e.preventDefault();
        // Stop this Escape from also bubbling to Modal's own document-level
        // Escape listener (modal.tsx) — without this, closing the popup would
        // close the whole Share modal in the same keystroke.
        e.stopPropagation();
        closeVisMenu(true);
        break;
      case "Tab":
        closeVisMenu(false);
        break;
      default:
        break;
    }
  }

  const [options, setOptions] = useState<EmbedOptions>(STANDARD_PRESET);
  const [widthUnit, setWidthUnit] = useState<"px" | "%">("%");
  const [widthPx, setWidthPx] = useState(640);
  const [height, setHeight] = useState(480);

  const width: number | "100%" = widthUnit === "%" ? "100%" : widthPx;
  const query = useMemo(() => buildEmbedQuery(options, { width, height }), [options, width, height]);
  const embedSrc = share ? `${share.embedUrl}?${query}` : "";
  const snippet = useMemo(() => {
    if (!share) return "";
    const safeTitle = mapTitle.replace(/"/g, "&quot;");
    const fullscreen = options.fullscreen ? " allowfullscreen" : "";
    return `<iframe src="${embedSrc}" width="${width}" height="${height}" style="border:0" loading="lazy" title="${safeTitle}"${fullscreen}></iframe>`;
  }, [share, embedSrc, width, height, mapTitle, options.fullscreen]);

  async function copyLink() {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — the URL stays selectable in the row
    }
  }

  async function run(kind: "visibility" | "rotate" | "revoke" | "export", action: () => Promise<void>) {
    setBusy(kind);
    try {
      await action();
    } finally {
      setBusy(null);
      if (kind === "revoke") setRevokeArmed(false);
    }
  }

  function close() {
    setRevokeArmed(false);
    onClose();
  }

  const toggleRows: Array<{ key: keyof EmbedOptions; label: string; hint?: string }> = [
    { key: "zoom", label: "Zoom controls" },
    { key: "panLock", label: "Pan lock", hint: "freeze the viewport" },
    { key: "filter", label: "Filter chips" },
    { key: "search", label: "Search" },
    { key: "legend", label: "Legend" },
    { key: "fullscreen", label: "Fullscreen" },
  ];

  // D8 (MAP-124): gate the Link/QR/Embed sections on `currentVisibility` —
  // the exact same reactive value the listbox trigger/hint already render
  // from (derived straight off the `share` prop, MAP-123) — so the sections
  // vanish/return in the same render as a visibility change lands, and stay
  // correct if the caller reverts `share.visibility` after a failed PATCH.
  // Matches the account SharePanel's `visibility !== "private"` gate.
  const isPrivate = currentVisibility === "private";
  const publishedLabel = share ? publishedAgo(share.lastPublishedAt) : null;

  const statusRow = share ? (
    <div className="share-status">
      <span aria-hidden className="share-status__dot" />
      <span>
        {isPrivate
          ? "Saved draft — not published"
          : `This map is live${publishedLabel ? ` — last published ${publishedLabel}` : ""}`}
      </span>
      {!isPrivate ? (
        <button
          type="button"
          className={cx("share-revoke", revokeArmed && "share-revoke--armed")}
          disabled={busy !== null}
          onClick={() => {
            if (!revokeArmed) {
              setRevokeArmed(true);
              return;
            }
            void run("revoke", onRevoke);
          }}
        >
          <Icon icon={Trash2} size="sm" />
          {busy === "revoke" ? "Revoking…" : revokeArmed ? "Really revoke?" : "Revoke link"}
        </button>
      ) : null}
    </div>
  ) : null;

  // The visibility control itself stays visible no matter what's selected
  // (Private included) — otherwise there'd be no way back out of Private
  // from inside the modal. Only the Link (URL/QR) and Embed sections below
  // gate on `isPrivate`.
  const visibilityControl = share ? (
    <div className="share-visibility">
      <p className="share-label">Who can see it</p>
      <div className="share-vis" ref={visContainerRef}>
        <button
          type="button"
          ref={visTriggerRef}
          className="share-vistrigger"
          aria-haspopup="listbox"
          aria-expanded={visMenuOpen}
          aria-controls={visListboxId}
          aria-label={`Visibility: ${VISIBILITY_OPTIONS.find((v) => v.value === currentVisibility)?.label ?? "Public"}`}
          disabled={busy !== null}
          onClick={onVisTriggerClick}
          onKeyDown={onVisTriggerKeyDown}
        >
          <span aria-hidden>{VISIBILITY_OPTIONS.find((v) => v.value === currentVisibility)?.label ?? "Public"}</span>
          <Icon icon={ChevronDown} size="sm" className="share-vischev" />
        </button>
        <div
          id={visListboxId}
          ref={visListboxRef}
          role="listbox"
          aria-label="Visibility options"
          aria-activedescendant={`${visListboxId}-opt-${VISIBILITY_OPTIONS[visHighlight]!.value}`}
          tabIndex={-1}
          hidden={!visMenuOpen}
          className="share-vislistbox"
          onKeyDown={onVisListboxKeyDown}
        >
          {VISIBILITY_OPTIONS.map((v, i) => {
            const gated = isGatedVisibility(v.value) && !isPaid;
            return (
              <div
                key={v.value}
                id={`${visListboxId}-opt-${v.value}`}
                role="option"
                aria-selected={currentVisibility === v.value}
                aria-label={gated ? `${v.label} (Pro feature)` : undefined}
                className={cx(
                  "share-visopt",
                  visHighlight === i && "share-visopt--active",
                  currentVisibility === v.value && "share-visopt--selected",
                )}
                onMouseEnter={() => setVisHighlight(i)}
                onClick={() => selectVisibility(v.value)}
              >
                <span className="share-visopt-main">
                  <span className="share-visopt-name">{v.label}</span>
                  <span className="share-visopt-hint">{v.hint}</span>
                </span>
                <span className="share-visopt-indicators">
                  {gated ? <span className="share-probadge">PRO</span> : null}
                  {currentVisibility === v.value ? <Icon icon={Check} size="sm" className="share-visopt-check" /> : null}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="share-hint">
        {VISIBILITY_HINTS[share.visibility] ?? VISIBILITY_HINTS.unlisted}
        {" "}
        <span className="share-hint__soon">Password protection is coming soon.</span>
      </p>
      <p className="share-eyeline">
        <Icon icon={Eye} size="sm" />
        Viewers see the map only — never your file.
      </p>
      <div className="share-togglerow share-togglerow--export">
        <span className={cx("share-togglerow__label", !viewersCanExport && "share-togglerow__label--off")}>
          Let viewers export
        </span>
        <span className="share-togglerow__hint">PNG · KML · PDF download</span>
        <Toggle
          checked={viewersCanExport}
          disabled={busy !== null}
          aria-label="Let viewers export the map"
          onChange={(e) => void run("export", () => onSetViewersCanExport(e.target.checked))}
        />
      </div>
      <div className="share-actions">
        <Button
          variant="ghost"
          size="sm"
          disabled={busy !== null}
          loading={busy === "rotate"}
          iconLeading={busy === "rotate" ? undefined : <Icon icon={RotateCcw} size="sm" />}
          onClick={() => void run("rotate", onRotate)}
        >
          Rotate link
        </Button>
        <span className="share-actions__hint">Rotating mints a fresh link and dead-ends this one.</span>
      </div>
    </div>
  ) : null;

  // D8 (MAP-124): the shareable URL + QR — withheld for Private the same way
  // SharePanel withholds its "Share link" CopyField, since a Private map's
  // link never resolves for anyone but the owner.
  const linkTab = share && !isPrivate ? (
    <div className="share-stack">
      {statusRow}

      <div className="share-linkrow">
        <Icon icon={signedIn ? Link2 : Lock} size="sm" className="share-linkrow__glyph" />
        <span className={cx("share-linkrow__url", !signedIn && "share-linkrow__url--masked")}>
          {signedIn ? share.url : maskShareUrl(share.url, share.slug)}
        </span>
        {signedIn ? (
          <Button size="sm" onClick={() => void copyLink()} iconLeading={<Icon icon={copied ? Check : Copy} size="sm" />}>
            {copied ? "Copied" : "Copy link"}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={() => {
              window.location.href = "/sign-up/";
            }}
            iconLeading={<Icon icon={Lock} size="sm" />}
          >
            Sign up to copy
          </Button>
        )}
      </div>

      <div className="share-grid">
        {visibilityControl}
        <div className="share-qr">
          {signedIn ? (
            <>
              <QrCode text={share.url} />
              <span className="share-qr__caption">Scan to open</span>
            </>
          ) : (
            <>
              <div className="share-qr__locked" aria-hidden>
                <Icon icon={Lock} size="lg" />
              </div>
              <span className="share-qr__caption">Sign up to share</span>
            </>
          )}
        </div>
      </div>
    </div>
  ) : null;

  // D8 (MAP-124): a Private map has no working link to embed, so the whole
  // Embed tab goes away rather than rendering an empty/misleading panel.
  const privateSection = share && isPrivate ? (
    <div className="share-stack">
      {statusRow}
      {visibilityControl}
      <p className="share-hint">Make it unlisted or public to get a shareable link and embed code.</p>
    </div>
  ) : null;

  const embedTab = share && !isPrivate ? (
    <div className="share-stack">
      <div className="share-preset">
        <p className="share-label">Preset</p>
        <SegmentedControl<"standard" | "store_locator">
          label="Embed preset"
          value={options.mode}
          onChange={(mode) => setOptions(mode === "store_locator" ? LOCATOR_PRESET : STANDARD_PRESET)}
          options={[
            { value: "standard", label: "Standard map" },
            { value: "store_locator", label: "Store locator" },
          ]}
        />
        {options.mode === "store_locator" && (
          <p className="share-hint">
            Search + radius + a results list glued to the map — the franchise-locator layout.
          </p>
        )}
      </div>

      <div className="share-embedgrid">
        <div className="share-controls">
          <p className="share-label">Controls</p>
          <div className="share-toggles">
            {toggleRows.map((row) => (
              <div key={row.key} className="share-togglerow">
                <span className={cx("share-togglerow__label", !options[row.key] && "share-togglerow__label--off")}>
                  {row.label}
                </span>
                {row.hint && <span className="share-togglerow__hint">{row.hint}</span>}
                <Toggle
                  checked={Boolean(options[row.key])}
                  aria-label={row.label}
                  onChange={(e) => setOptions((prev) => ({ ...prev, [row.key]: e.target.checked }))}
                />
              </div>
            ))}
            <div className="share-togglerow">
              <span className="share-togglerow__label">Attribution</span>
              <Badge variant="accent" className="share-togglerow__badge">
                always on
              </Badge>
              <Toggle checked disabled aria-label="Attribution (always on)" onChange={() => undefined} />
            </div>
          </div>

          <p className="share-label share-label--size">Size</p>
          <div className="share-sizerow">
            <span className="share-sizerow__name">Width</span>
            {widthUnit === "px" ? (
              <Input
                mono
                type="number"
                min={200}
                className="share-sizerow__input"
                value={widthPx}
                aria-label="Embed width in pixels"
                onChange={(e) => setWidthPx(Math.max(0, Number(e.target.value) || 0))}
              />
            ) : (
              <span className="share-sizerow__fixed">100</span>
            )}
            <SegmentedControl<"px" | "%">
              label="Width unit"
              value={widthUnit}
              onChange={setWidthUnit}
              options={[
                { value: "px", label: "px" },
                { value: "%", label: "%" },
              ]}
            />
          </div>
          <div className="share-sizerow">
            <span className="share-sizerow__name">Height</span>
            <Input
              mono
              type="number"
              min={160}
              className="share-sizerow__input"
              value={height}
              aria-label="Embed height in pixels"
              onChange={(e) => setHeight(Math.max(0, Number(e.target.value) || 0))}
            />
            <span className="share-sizerow__unit">px</span>
          </div>
        </div>

        <div className="share-preview">
          <div className="share-preview__head">
            <span className="share-label">Live preview</span>
            <span className="share-preview__note">updates as you flip</span>
            <span className="share-preview__dims">
              {width === "100%" ? "100%" : `${width}px`} × {height}
            </span>
          </div>
          <div className="share-preview__frame">
            <iframe
              key={embedSrc}
              src={embedSrc}
              title={`${brandName} embed preview`}
              loading="lazy"
              className="share-preview__iframe"
              style={{ width: width === "100%" ? "100%" : Math.min(widthPx, 9999) }}
            />
          </div>
        </div>
      </div>

      <EmbedSnippet code={snippet} label="Copy-paste snippet" onCopy={onSnippetCopy} />
      <p className="share-snippetnote">
        The querystring mirrors your toggles — the snippet rewrites itself as you flip them.
      </p>
    </div>
  ) : null;

  return (
    <Modal open={open} onClose={close} title="Share this map" className="share-modal">
      {minting && (
        <div className="share-pending">
          <Spinner size="lg" />
          <p className="share-pending__title">Publishing your map…</p>
          <p className="share-pending__note">Minting a live link — one moment.</p>
        </div>
      )}
      {!minting && mintError && (
        <div className="share-pending">
          <p className="share-pending__title">Couldn&rsquo;t publish this map</p>
          <p className="share-pending__note">{mintError}</p>
          <Button onClick={onRetry}>Try again</Button>
        </div>
      )}
      {!minting && !mintError && share && isPrivate && privateSection}
      {!minting && !mintError && share && !isPrivate && (
        <Tabs
          items={[
            { id: "link", label: "Link", content: linkTab },
            { id: "embed", label: "Embed", content: embedTab },
          ]}
        />
      )}
    </Modal>
  );
}
